import { TargetingType } from "./targeting.types";

/**
 * Recipient channel preference. Mirrors the union of the same name in
 * `@punchup/ui-common/components/messaging`; defined locally here because
 * `@punchup/data` must not depend on `@punchup/ui-common` (ui-common already
 * depends on data — importing it back would create a cycle). The two unions are
 * structurally identical, so values remain interchangeable across the boundary.
 */
export type ChannelPreference = "email_only" | "sms_only" | "email_and_sms";

/**
 * `southpaw_jobs.email_template` sentinel marking a job whose `email_body`
 * holds sanitized rich HTML in the body vocabulary
 * (`@punchup/messaging/models/body-html-vocabulary` — p/ul/ol/li/blockquote +
 * b/i/u/a/span-style), produced by the venue composer's rich text editor and
 * sanitized at the write boundary (`create-job` / the v2 job-update route).
 * NOT a full pre-rendered document: unlike `NEWSLETTER_JOB_EMAIL_TEMPLATE`
 * (`newsletter.types.ts`), these jobs still flow through
 * `genVenueSouthpawEmailTemplate`, which renders the body via
 * `@punchup/messaging/server/render-body-email-html` instead of the legacy
 * newline-splitting. Plain-text jobs keep `email_template: "none"` and render
 * exactly as before. Exported from models so client surfaces (composer,
 * preview, job history) can read it without crossing the server boundary.
 */
export const RICH_BODY_EMAIL_TEMPLATE = "body_inline_html_v1" as const;

/** `email_template` of a plain-text job — the only comedian job a manager can edit after scheduling. */
export const PLAIN_BODY_EMAIL_TEMPLATE = "none" as const;

/** A subject or body must be at least this long; create-job and the scheduled edit both enforce it. */
export const EMAIL_SUBJECT_MIN_LENGTH = 7;
export const EMAIL_BODY_MIN_LENGTH = 33;

/** An SMS body must be at least this long; the scheduled edit enforces it server-side. */
export const SMS_MIN_LENGTH = 10;

/** Per-field minimums, trimmed — the composer's field notes and the validity gates below share them. */
export const isEmailSubjectLongEnough = (subject: string): boolean =>
  subject.trim().length >= EMAIL_SUBJECT_MIN_LENGTH;

export const isEmailBodyLongEnough = (body: string): boolean =>
  body.trim().length >= EMAIL_BODY_MIN_LENGTH;

export const isSmsLongEnough = (smsBody: string): boolean =>
  smsBody.trim().length >= SMS_MIN_LENGTH;

/** The email meets the minimums (trimmed) and has a header image; the composer and the scheduled edit gate on it. */
export function isEmailContentValid({
  subject,
  body,
  hasImage,
}: {
  subject: string;
  body: string;
  hasImage: boolean;
}): boolean {
  return (
    isEmailSubjectLongEnough(subject) && isEmailBodyLongEnough(body) && hasImage
  );
}

/**
 * Email/SMS message data structure
 */
export interface EmailData {
  comedianId: string;
  comedianName: string;
  subject: string | null;
  message: string | null;
  smsBody?: string | null; // Custom SMS body (when different from email body)
  channelPreference?: ChannelPreference | null;
  // When true, restrict the send to first-party Punchup subscribers via email
  // only — no SMS, no third-party/imported contacts. Mirrors the "FREE: Only
  // Email Punchup Subscribers" toggle so the server can enforce it.
  onlySubscribers?: boolean;
  scheduledDate: string | null; // ISO datetime string
  venueIds: string[];
  showIds?: string[]; // For waitlist targeting
  contactListIds?: string[]; // For contact list targeting
  fansSelected: number;
  image: {
    id: string; // Cloudflare image ID
    url: string;
    isPunchupImage: boolean;
    file?: File; // Optional File object for uploads
  } | null;
}

/**
 * Targeting option with user counts and credit estimation
 */
export interface TargetingOption {
  id: string;
  title: string;
  description: string;
  icon: string;
  targetingType: TargetingType; // Maps to southpaw TargetingType enum
  userCount?: number;
  estimatedCredits?: number;
  requiresVenueSelection?: boolean;
  requiresShowSelection?: boolean;
  requiresContactListSelection?: boolean;
  creditRates?: {
    email: number;
    sms: number;
  };
  // For guest list variants
  followedAfterDate?: string;
  followedBeforeDate?: string;
}

/**
 * Targeting category (top-level grouping)
 */
export interface TargetingCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  subcategories: TargetingOption[];
}

/**
 * Venue data with fan counts
 */
export interface VenueWithFanData {
  venue_id: string;
  venue_name: string;
  city: string;
  state: string;
  next_show: string; // ISO datetime
  users_within_50_miles: number;
  total_reachable_emails: number | null;
  total_reachable_phones: number | null;
  lastScheduledDate?: string | null;
}

/**
 * Venue data with combined fan + imported-contact counts for a CUSTOM radius
 * (10/25/75/100 mi). Sourced from comedian_fans_near_venues_v2 +
 * comedian_contacts_near_venues_v2 via get_audience_near_venues_by_radius.
 *
 * Counts are split fan-side vs. contact-side so the UI can display the imported
 * contributions alongside the subscriber numbers (and so cost estimation can
 * keep deriving paid email count = total emails − subscriber emails). Sum the
 * pairs to get totals.
 */
export interface VenueWithAudienceDataAtRadius {
  venue_id: string;
  venue_name: string;
  city: string;
  state: string;
  next_show: string; // ISO datetime
  fans_within_radius: number;
  fan_reachable_emails: number | null;
  fan_reachable_phones: number | null;
  contacts_within_radius: number;
  contact_reachable_emails: number | null;
  contact_reachable_phones: number | null;
}

/**
 * Show data for waitlist targeting
 */
export interface ShowWithWaitlistData {
  id: string;
  comedian_id: string;
  datetime: string; // ISO datetime
  venue: string | null;
  location: string | null;
  venue_id: string | null;
  waitlistCount: number;
  emailCount: number;
  phoneCount: number;
  waitlistUserIds: string[]; // user_ids on this show's waitlist for deduplication
  waitlistUserIdsWithEmail: string[]; // user_ids with email for cross-show deduplication
  waitlistUserIdsWithPhone: string[]; // user_ids with phone for cross-show deduplication
  created_at?: string; // ISO datetime, for "most recently created" sort
  is_sold_out?: boolean; // For filtering sold-out vs available shows
}

/**
 * Venue with grouped shows for waitlist display
 */
export interface VenueWithGroupedShows {
  venue_id: string;
  venue_name: string;
  city: string | null;
  state: string | null;
  shows: ShowWithWaitlistData[];
  totalWaitlistCount: number;
  totalEmailCount: number;
  totalPhoneCount: number;
}

/**
 * Contact list data for imported audience targeting
 */
export interface ContactListWithAudienceData {
  id: string;
  name: string;
  numContacts: number;
  emailCount: number;
  phoneCount: number;
  numSubscribed: number;
}

/**
 * Targeting parameters that can vary by segment type
 */
export interface TargetingParameters {
  venueIds?: string[];
  radiusMiles?: number;
  followedAfterDate?: string;
  followedBeforeDate?: string;
  showIds?: string[];
  contactListIds?: string[];
  allContactLists?: boolean; // If true, target all contacts for the comedian
  createdBefore?: string; // Filter contacts created before this date (ISO 8601)
  createdAfter?: string; // Filter contacts created after this date (ISO 8601)
  // Venue proximity filter for CONTACT_LIST targeting (uses comedian_contacts_near_venues)
  nearVenueIds?: string[]; // If provided, only include contacts near these venues
}

/**
 * A single targeting segment within a composite audience
 */
export interface AudienceSegment {
  id: string; // Segment ID (e.g., "all_followers", "fans_near_shows", "imported_lists")
  type: TargetingType; // VENUE=1, GUEST_LIST=2, WAITLIST=3, CONTACT_LIST=4
  parameters: TargetingParameters;
}

/**
 * Composite targeting configuration for multi-segment audiences
 */
export interface CompositeTargetingConfig {
  segments: AudienceSegment[];
}

/**
 * Payload for creating a messaging job
 */
export interface CreateMessagingJobPayload {
  comedianId: string;
  scheduledForDatetimeEST: string; // Format: "YYYY-MM-DD HH:mm"
  emailSubject: string;
  emailBody: string;
  smsBody?: string; // Custom SMS body (when different from email body)
  channelPreference?: ChannelPreference;
  // When true, the server restricts the send to first-party subscribers via
  // email only (no SMS, no third-party contacts). Defaults to false when absent.
  onlySubscribers?: boolean;
  heroImageCFID: string;
  targetingType?: TargetingType; // Optional when using compositeTargeting
  targetingParameters?: TargetingParameters;
  // NEW: Composite targeting for multi-segment audiences
  compositeTargeting?: CompositeTargetingConfig;
  // Legacy fields for backward compatibility (type 1 only)
  venueIds?: string[];
  contactListIds?: string[];
}

/**
 * Audience statistics from job creation
 */
export interface AudienceStats {
  totalUsersToReach: number;
  totalNumEmails: number;
  totalNumTexts: number;
}

/**
 * Response from creating a messaging job
 */
export interface CreateMessagingJobResponse {
  message: string;
  jobId?: string;
  error?: string;
  results?: {
    audienceStats: AudienceStats;
    targetingType: number;
    venueIds?: string[];
    usedLegacyFormat?: boolean;
  };
}

/**
 * Guest list audience counts (lightweight alternative to full audience fetch)
 * Used by the UI to display fan/email/phone counts without loading all rows
 */
export interface GuestListCounts {
  fanCount: number;
  emailCount: number;
  phoneCount: number;
  bothCount: number;
}

/**
 * Guest list audience member
 */
export interface GuestListAudienceMember {
  user_id: string;
  email: string | null;
  phone: string | null;
}

/**
 * Waitlist audience member (includes show_id)
 */
export interface WaitlistAudienceMember {
  user_id: string;
  email: string | null;
  phone: string | null;
  show_id: string;
}

/**
 * Email job row (mapped from southpaw_jobs table)
 */
export interface EmailJobRow {
  id: string;
  comedian_id: string;
  created_by: string;
  created_at: string;
  email_subject: string;
  email_body: string;
  sms_body: string | null;
  email_template: string;
  hero_image: string;
  venue_ids: string[];
  contact_list_ids: string[];
  scheduled_for: string;
  status: string;
  targeting_type: number;
  total_users_to_reach: number | null;
  total_num_emails: number | null;
  total_num_texts: number | null;
  num_users_to_email: number;
  campaign_name: string | null;
  composite_targeting?: CompositeTargetingConfig | null;
  // Map of venue UUID -> venue display name (resolved by jobs API)
  venue_names?: Record<string, string>;
  // Analytics aggregated from archive tables
  email_opened_count?: number;
  email_clicked_count?: number;
  email_delivered_count?: number;
  sms_clicked_count?: number;
  sms_delivered_count?: number;
  total_clicked_count?: number;
  email_failed_count?: number;
  total_failed_count?: number;
}

/**
 * Response from fetching email jobs
 */
export interface FetchEmailJobsResponse {
  jobs: EmailJobRow[];
}

/**
 * Image from comedian's Punchup library
 * Includes images from about_img, signature_img, and special preview images
 */
export interface LibraryImage {
  id: string; // Cloudflare image ID
  url: string; // Full CDN URL
  type: "about" | "signature" | "special";
}

/**
 * Imported contacts near venues data
 * Used by the "All" targeting category to show combined subscriber + imported counts
 */
export interface ImportedContactsNearVenuesData {
  totalImportedContacts: number;
  totalWithin50Miles: number;
  totalReachableEmails: number;
  totalReachablePhones: number;
  venues: Array<{
    venue_id: string;
    venue_name: string;
    imported_within_50_miles: number;
    imported_reachable_emails: number;
    imported_reachable_phones: number;
  }>;
}

/**
 * Subcategory data item with optional split counts for "All" category combined display
 */
export interface SubcategoryDataItem {
  count: number;
  emailCount: number;
  phoneCount: number;
  // Split counts for "All" category combined display ("{x} subscribers · {x} imported")
  subscriberCount?: number;
  importedCount?: number;
}
